import { useEffect, useState, useCallback, useMemo } from "react";
import { api, extractError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Checkbox } from "../../components/ui/checkbox";
import { Plus, Pencil, Power } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["credit", "efs", "debit"];
const EMPTY = { label: "", last4: "", type: "credit", shared: false, assigned_user_ids: [], active: true };

function PageHeader({ title, subtitle, action }) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export default function CardsAdmin() {
  const [cards, setCards] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [c, u] = await Promise.all([
        api.get("/cards", { params: { active_only: false } }),
        api.get("/users", { params: { active_only: false } }),
      ]);
      setCards(c.data);
      setUsers(u.data);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const usersById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const workers = useMemo(() => users.filter((u) => u.role === "worker"), [users]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setFormError(""); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      label: c.label, last4: c.last4, type: c.type, shared: c.shared,
      assigned_user_ids: c.assigned_user_ids || [], active: c.active,
    });
    setFormError(""); setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setFormError("");
    try {
      if (editing) {
        await api.patch(`/cards/${editing.id}`, form);
        toast.success("Card updated");
      } else {
        await api.post("/cards", form);
        toast.success("Card created");
      }
      setOpen(false); load();
    } catch (e) { setFormError(extractError(e)); }
    finally { setSubmitting(false); }
  };

  const deactivate = async (c) => {
    if (!window.confirm(`Deactivate ${c.label}?`)) return;
    try { await api.delete(`/cards/${c.id}`); toast.success("Card deactivated"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  const toggleAssigned = (uid) => {
    setForm((f) => {
      const has = f.assigned_user_ids.includes(uid);
      return { ...f, assigned_user_ids: has ? f.assigned_user_ids.filter((x) => x !== uid) : [...f.assigned_user_ids, uid] };
    });
  };

  return (
    <div data-testid="cards-admin">
      <PageHeader
        title="Cards"
        subtitle="Credit, EFS fuel, and debit cards. Shared cards can be assigned to multiple workers."
        action={
          <Button onClick={openCreate} data-testid="create-card-btn" className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
            <Plus className="mr-1 h-4 w-4" /> New card
          </Button>
        }
      />
      {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

      <div className="rounded-md border border-slate-200 bg-white">
        <Table data-testid="cards-table">
          <TableHeader>
            <TableRow>
              <TableHead>Label</TableHead>
              <TableHead>Last 4</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Shared</TableHead>
              <TableHead>Assigned</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-500">Loading…</TableCell></TableRow>
            ) : cards.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-500">No cards</TableCell></TableRow>
            ) : (
              cards.map((c) => (
                <TableRow key={c.id} data-testid={`card-row-${c.last4}`}>
                  <TableCell className="font-medium">{c.label}</TableCell>
                  <TableCell className="font-mono">{c.last4}</TableCell>
                  <TableCell><Badge variant="secondary" className="bg-[#0F1E3D]/5 text-[#0F1E3D]">{c.type}</Badge></TableCell>
                  <TableCell>{c.shared ? "Yes" : "No"}</TableCell>
                  <TableCell className="max-w-xs truncate text-sm text-slate-600">
                    {(c.assigned_user_ids || []).map((id) => usersById[id]?.email || id).join(", ") || "—"}
                  </TableCell>
                  <TableCell>
                    {c.active
                      ? <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50">Active</Badge>
                      : <Badge variant="secondary" className="bg-slate-100 text-slate-600">Inactive</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-card-${c.last4}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {c.active && (
                      <Button variant="ghost" size="sm" onClick={() => deactivate(c)} data-testid={`deactivate-card-${c.last4}`}>
                        <Power className="h-4 w-4 text-rose-600" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="card-dialog" className="max-w-lg">
          <form onSubmit={submit} className="space-y-4">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit card" : "New card"}</DialogTitle>
              <DialogDescription>Registry entry for a payment card.</DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="c-label">Label</Label>
                <Input id="c-label" required value={form.label}
                  onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                  data-testid="card-label-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="c-last4">Last 4</Label>
                <Input id="c-last4" required maxLength={8} value={form.last4}
                  onChange={(e) => setForm((f) => ({ ...f, last4: e.target.value }))}
                  data-testid="card-last4-input" />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                  <SelectTrigger data-testid="card-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPES.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between rounded-md border border-slate-200 p-3">
              <div>
                <Label>Shared</Label>
                <p className="text-xs text-slate-500">Multiple workers can use this card.</p>
              </div>
              <Switch checked={form.shared}
                onCheckedChange={(v) => setForm((f) => ({ ...f, shared: v }))}
                data-testid="card-shared-switch" />
            </div>

            <div className="space-y-2">
              <Label>Assigned workers</Label>
              <div className="max-h-44 space-y-2 overflow-y-auto rounded-md border border-slate-200 p-3" data-testid="card-assignees">
                {workers.length === 0 ? (
                  <p className="text-xs text-slate-500">No workers available.</p>
                ) : workers.map((w) => (
                  <label key={w.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form.assigned_user_ids.includes(w.id)}
                      onCheckedChange={() => toggleAssigned(w.id)}
                      data-testid={`card-assignee-${w.email}`}
                    />
                    <span>{w.email}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between rounded-md border border-slate-200 p-3">
              <div>
                <Label>Active</Label>
                <p className="text-xs text-slate-500">Inactive cards hidden from workers.</p>
              </div>
              <Switch checked={form.active}
                onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))}
                data-testid="card-active-switch" />
            </div>

            {formError && <Alert variant="destructive"><AlertDescription>{formError}</AlertDescription></Alert>}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="card-submit-btn"
                className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
                {submitting ? "Saving…" : editing ? "Save changes" : "Create card"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
